import React, { useEffect, useState } from 'react';
import { FaDownload, FaFileAlt, FaFileImage, FaFilePdf, FaFileWord, FaPlus, FaTrash } from 'react-icons/fa';
import api from '../services/apiService';
import { useNotification } from './NotificationProvider';
import '../scss/teamDocuments.scss';

const createEmptyPendingDocument = () => ({
  key: Date.now() + Math.random(),
  title: '',
  file: null
});

const getDocumentUrl = (filePath) => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${base}/${String(filePath || '').replace(/^\//, '')}`;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentTypeIcon = ({ fileType }) => {
  if (fileType === 'pdf') {
    return <FaFilePdf />;
  }
  if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') {
    return <FaFileImage />;
  }
  return <FaFileWord />;
};

const TeamDocumentsSection = ({ teamId, heading = 'h2', showHeading = true, assumeCanManage = false }) => {
  const [documents, setDocuments] = useState([]);
  const [academyDocuments, setAcademyDocuments] = useState([]);
  const [canManageDocuments, setCanManageDocuments] = useState(Boolean(assumeCanManage));
  const [pendingDocuments, setPendingDocuments] = useState([createEmptyPendingDocument()]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!teamId) {
        return;
      }
      try {
        const response = await api.get(`get_team_documents.php?team_id=${teamId}`);
        if (response.data.success) {
          setDocuments(response.data.documents || []);
          setAcademyDocuments(response.data.academy_documents || []);
          setCanManageDocuments(Boolean(response.data.can_manage) || Boolean(assumeCanManage));
        }
      } catch (err) {
        console.warn('Could not load team documents', err);
        setDocuments([]);
        setAcademyDocuments([]);
        setCanManageDocuments(false);
      }
    };

    fetchDocuments();
  }, [teamId, assumeCanManage]);

  const handlePendingDocumentChange = (key, field, value) => {
    setPendingDocuments(prev => prev.map(doc => (
      doc.key === key ? { ...doc, [field]: value } : doc
    )));
  };

  const handleAddPendingDocument = () => {
    setPendingDocuments(prev => [...prev, createEmptyPendingDocument()]);
  };

  const handleRemovePendingDocument = (key) => {
    setPendingDocuments(prev => (
      prev.length === 1 ? [createEmptyPendingDocument()] : prev.filter(doc => doc.key !== key)
    ));
  };

  const handleUploadDocuments = async (e) => {
    e.preventDefault();
    const readyDocuments = pendingDocuments.filter(doc => doc.title.trim() || doc.file);
    if (readyDocuments.length === 0) {
      showError('Add a title and choose a PDF or DOCX file to upload.');
      return;
    }

    const invalid = readyDocuments.find(doc => !doc.title.trim() || !doc.file);
    if (invalid) {
      showError('Each document needs a title and a PDF or DOCX file.');
      return;
    }

    setIsUploadingDocuments(true);
    try {
      const uploaded = [];
      for (const doc of readyDocuments) {
        const formData = new FormData();
        formData.append('team_id', teamId);
        formData.append('title', doc.title.trim());
        formData.append('file', doc.file);

        const response = await api.post('upload_team_document.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Upload failed');
        }
        uploaded.push(response.data.document);
      }

      setDocuments(prev => [...uploaded, ...prev]);
      setPendingDocuments([createEmptyPendingDocument()]);
      showSuccess(uploaded.length === 1 ? 'Document uploaded.' : `${uploaded.length} documents uploaded.`);
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Could not upload the document.');
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (documentItem) => {
    if (!window.confirm(`Delete "${documentItem.title}"? This cannot be undone.`)) {
      return;
    }

    setDeletingDocumentId(documentItem.document_id);
    try {
      const response = await api.post('delete_team_document.php', {
        document_id: documentItem.document_id
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Delete failed');
      }
      setDocuments(prev => prev.filter(item => item.document_id !== documentItem.document_id));
      showSuccess('Document deleted.');
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Could not delete the document.');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const listedDocuments = [
    ...academyDocuments.map((documentItem) => ({ ...documentItem, source: documentItem.source || 'club' })),
    ...documents.map((documentItem) => ({ ...documentItem, source: 'team' })),
  ];

  if (listedDocuments.length === 0 && !assumeCanManage) {
    return null;
  }

  const HeadingTag = heading === 'h3' ? 'h3' : 'h2';

  return (
    <section className="team-documents documents-section">
      {showHeading && (
        <HeadingTag className="section-title">
          <FaFileAlt className="section-icon" /> Team Documents
        </HeadingTag>
      )}

      {listedDocuments.length > 0 ? (
        <div className="documents-list">
          {listedDocuments.map(documentItem => (
            <div key={`${documentItem.source}-${documentItem.document_id}`} className="document-card">
              <div className="document-icon">
                <DocumentTypeIcon fileType={documentItem.file_type} />
              </div>
              <div className="document-details">
                <div className="document-title">{documentItem.title}</div>
                {documentItem.description ? (
                  <div className="document-description">{documentItem.description}</div>
                ) : null}
                <div className="document-meta">
                  {documentItem.source === 'club' ? 'Club · ' : ''}
                  {String(documentItem.file_type || '').toUpperCase()}
                  {documentItem.file_size ? ` · ${formatFileSize(documentItem.file_size)}` : ''}
                </div>
              </div>
              <div className="document-actions">
                <a
                  href={getDocumentUrl(documentItem.file_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="document-action"
                >
                  <FaDownload /> View
                </a>
                {canManageDocuments && documentItem.source !== 'club' && (
                  <button
                    type="button"
                    className="document-action danger"
                    onClick={() => handleDeleteDocument(documentItem)}
                    disabled={deletingDocumentId === documentItem.document_id}
                  >
                    <FaTrash /> {deletingDocumentId === documentItem.document_id ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-documents-message">No documents have been uploaded yet.</p>
      )}

      {assumeCanManage && (
        <form className="document-upload-form" onSubmit={handleUploadDocuments}>
          <h3 className="upload-title">Upload documents</h3>
          <p className="upload-help">PDF or Word (DOCX) files only. Add a title for each file.</p>
          {pendingDocuments.map((doc) => (
            <div key={doc.key} className="pending-document-row">
              <div className="form-group">
                <label htmlFor={`document-title-${doc.key}`}>Title (name)</label>
                <input
                  id={`document-title-${doc.key}`}
                  type="text"
                  value={doc.title}
                  onChange={(e) => handlePendingDocumentChange(doc.key, 'title', e.target.value)}
                  placeholder="e.g. Code of Conduct"
                  maxLength={255}
                />
              </div>
              <div className="form-group">
                <label htmlFor={`document-file-${doc.key}`}>File</label>
                <div className="file-picker">
                  <input
                    id={`document-file-${doc.key}`}
                    className="file-picker-input"
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => handlePendingDocumentChange(doc.key, 'file', e.target.files?.[0] || null)}
                  />
                  <span className="file-picker-button">Choose file</span>
                  <span className="file-picker-name">
                    {doc.file ? doc.file.name : 'No file chosen'}
                  </span>
                </div>
              </div>
              {pendingDocuments.length > 1 && (
                <button
                  type="button"
                  className="remove-pending-button"
                  onClick={() => handleRemovePendingDocument(doc.key)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className="upload-actions">
            <button
              type="button"
              className="add-document-button"
              onClick={handleAddPendingDocument}
            >
              <FaPlus /> Add another document
            </button>
            <button
              type="submit"
              className="upload-documents-button"
              disabled={isUploadingDocuments}
            >
              {isUploadingDocuments ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default TeamDocumentsSection;
